import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/api/client";
import { Building2, Globe, PlusCircle, Sparkles } from "lucide-react";

const CATEGORIES = [
  "Affiliate Marketing", "E-commerce", "Dropshipping", "Blogging", "Digital Products",
  "Online Courses", "SaaS & AI Tools", "Travel & Tourism", "Real Estate",
  "Healthcare & Fitness", "Finance & Crypto",
];
const COUNTRIES = ["United States", "United Kingdom", "Germany", "France", "India", "Japan", "Australia", "Canada", "Brazil", "Singapore", "UAE"];
const LANGUAGES = ["English", "Hindi", "Marathi", "Gujarati", "Tamil", "Telugu", "Bengali", "Kannada", "Punjabi", "Malayalam", "Spanish", "French", "German", "Japanese", "Portuguese", "Arabic"];

export default function Businesses() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    business_name: "", business_category: "Affiliate Marketing",
    country: "United States", language: "English", domain: "", affiliate_id: "",
  });

  const load = async () => {
    try { const { data } = await api.get("/businesses"); setItems(data); } catch {}
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(""); setCreating(true);
    try {
      await api.post("/business/create", form);
      setShowForm(false);
      setForm({ ...form, business_name: "", domain: "", affiliate_id: "" });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-8 fade-up">
      <div className="flex items-end justify-between border-b border-slate-800 pb-6">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-2">— Portfolio</p>
          <h1 className="font-serif text-5xl leading-none text-sky-100 tracking-tight">Businesses</h1>
          <p className="mt-3 text-neutral-400 max-w-2xl">Every autonomous site your AI runs, in one place.</p>
        </div>
        <button
          data-testid="btn-toggle-launcher"
          onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-2 bg-sky-400 hover:bg-sky-300 text-slate-950 px-4 py-2.5 rounded-md text-sm font-medium"
        >
          <PlusCircle className="w-4 h-4" /> {showForm ? "Cancel" : "Launch new"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} data-testid="launcher-form" className="border border-sky-500/40 rounded-xl p-8 bg-gradient-to-br from-sky-500/5 to-transparent space-y-6">
          <h2 className="font-serif text-2xl text-sky-100">Launch a new business</h2>
          {error && <div className="border border-red-500/40 bg-red-500/5 text-red-300 px-4 py-2.5 text-sm rounded">{error}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-2">Business name</label>
              <input required data-testid="input-biz-name" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Nova Fitness Co." className="w-full bg-transparent border-b border-slate-700 focus:border-sky-400 py-3 text-sky-50 outline-none" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-2">Category</label>
              <select data-testid="select-biz-category" value={form.business_category} onChange={(e) => setForm({ ...form, business_category: e.target.value })} className="w-full bg-slate-900 border border-slate-800 rounded py-2.5 px-3 text-sky-50 focus:border-sky-400 outline-none">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-2">Country</label>
              <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="w-full bg-slate-900 border border-slate-800 rounded py-2.5 px-3 text-sky-50 focus:border-sky-400 outline-none">
                {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-2">Language</label>
              <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} className="w-full bg-slate-900 border border-slate-800 rounded py-2.5 px-3 text-sky-50 focus:border-sky-400 outline-none">
                {LANGUAGES.map((l) => <option key={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-2">Domain (optional)</label>
              <input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} placeholder="mybrand.com" className="w-full bg-transparent border-b border-slate-700 focus:border-sky-400 py-3 text-sky-50 outline-none" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-2">Affiliate ID (optional)</label>
              <input value={form.affiliate_id} onChange={(e) => setForm({ ...form, affiliate_id: e.target.value })} placeholder="mybrand-20" className="w-full bg-transparent border-b border-slate-700 focus:border-sky-400 py-3 text-sky-50 outline-none" />
            </div>
          </div>
          <button data-testid="btn-launch-biz" type="submit" disabled={creating} className="w-full py-3.5 bg-sky-400 hover:bg-sky-300 text-slate-950 font-medium rounded-md flex items-center justify-center gap-2 disabled:opacity-60">
            {creating ? "Building…" : <><Sparkles className="w-4 h-4" /> Launch instantly</>}
          </button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && !showForm && (
          <p className="col-span-full text-neutral-500 text-center py-16">No businesses yet. Launch your first one.</p>
        )}
        {items.map((biz) => (
          <div key={biz.id} data-testid={`biz-card-${biz.id}`} className="border border-slate-800 rounded-lg p-5 hover:border-sky-400/40 transition">
            <div className="flex items-start justify-between mb-3">
              <Building2 className="w-5 h-5 text-sky-400" />
              <span className="text-[10px] font-mono uppercase text-emerald-400">● Live · {biz.currency}</span>
            </div>
            <h3 className="font-serif text-lg text-sky-100">{biz.business_name}</h3>
            <p className="text-[11px] font-mono text-neutral-500 mt-1">{biz.business_category}</p>
            <div className="mt-4 pt-4 border-t border-slate-800 space-y-1.5 text-xs">
              <p className="flex items-center gap-2 text-neutral-400"><Globe className="w-3 h-3" /> {biz.domain}</p>
              <p className="text-neutral-500">{biz.country} · {biz.language}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
